import { CheckedAddress } from '@sparkdotfi/common-universal'
import { ChevronUp, ScanEyeIcon } from 'lucide-react'
import { ButtonHTMLAttributes, forwardRef } from 'react'

import { useStore } from '@/domain/state'
import { WalletDropdownTriggerInfo } from '@/features/topbar/types'
import { Button, ButtonIcon } from '@/ui/atoms/button/Button'
import { cn } from '@/ui/utils/style'
import { testIds } from '@/ui/utils/testIds'

export interface TopbarWalletButtonProps extends ButtonHTMLAttributes<HTMLButtonElement>, WalletDropdownTriggerInfo {
  open: boolean
}

// forwarding ref so dropdown menu trigger works with asChild
export const TopbarWalletButton = forwardRef<HTMLButtonElement, TopbarWalletButtonProps>(
  ({ mode, avatar, address, ensName, open, ...props }, ref) => {
    const buttonProps = {
      ...props,
      className: 'w-full flex items-center justify-start gap-2.5',
      variant: 'tertiary',
      size: 'm',
      ref,
    } as const

    const sandboxNetwork = useStore((state) => state.sandbox.network)

    if (mode === 'sandbox') {
      return (
        <Button {...buttonProps} className={cn(buttonProps.className, 'pointer-events-none justify-center gap-1')}>
          {sandboxNetwork?.ephemeralAccountPrivateKey ? 'Sandbox' : CheckedAddress.formatShort(address)}
        </Button>
      )
    }

    const chevron = (
      <ButtonIcon
        icon={ChevronUp}
        className={cn(
          'hidden md:block',
          open ? 'rotate-0 text-brand' : 'rotate-180 text-secondary',
          'icon-size-xs! ml-auto',
        )}
      />
    )

    const avatarContent =
      mode === 'watch-wallet' ? (
        <div className="icon-size-md flex items-center justify-center rounded-full bg-primary">
          <ScanEyeIcon className="icon-size-sm text-secondary" />
        </div>
      ) : (
        <img src={avatar} alt="wallet-avatar" className="icon-size-md rounded-full" />
      )

    const displayName = ensName ? ensName : CheckedAddress.formatShort(address)

    return (
      <Button {...buttonProps} data-testid={testIds.component.TopbarWalletButton}>
        {avatarContent}
        <div className="hidden truncate sm:block">{displayName}</div>
        {chevron}
      </Button>
    )
  },
)

TopbarWalletButton.displayName = 'TopbarWalletButton'
